import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowLeft, MapPin, Phone } from 'lucide-react';
import { brandTheme } from '../../styles/BrandTheme';
import { trackBlogView } from '../../utils/gtag';
import HeroSectionComponent from '../../components/HeroSection';
import {
  getLondonGuideContext,
  getAllLondonGuideArticleSummaries,
  getRelatedLondonGuides,
  isValidServiceAreaRouteSlug,
} from '../../data/serviceAreaBlogMapping';
import {
  buildGuideMetaDescription,
  buildGuideKeywords,
  getLocalLeadParagraph,
  buildGuideFaqs,
} from '../../data/boroughGuideContent';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${brandTheme.colors.primary[600]};
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 500;

  &:hover {
    color: ${brandTheme.colors.primary[700]};
    gap: 0.75rem;
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 2rem;

  .category {
    display: inline-block;
    background: ${brandTheme.colors.primary[100]};
    color: ${brandTheme.colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${brandTheme.borderRadius.full};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: clamp(1.75rem, 4vw, 2.75rem);
    line-height: 1.2;
    margin-bottom: 1rem;
    color: ${brandTheme.colors.secondary[900]};
  }

  .excerpt {
    font-size: 1.15rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: 1.6;
  }

  .meta {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-top: 1.5rem;
    color: ${brandTheme.colors.neutral.gray[500]};
    font-size: 0.95rem;
    align-items: center;
  }
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${brandTheme.colors.secondary[700]};

  h2 {
    font-size: 1.65rem;
    margin: 2.5rem 0 1rem;
    color: ${brandTheme.colors.secondary[900]};
  }

  h3 {
    font-size: 1.25rem;
    margin: 1.75rem 0 0.75rem;
    color: ${brandTheme.colors.secondary[900]};
  }

  p {
    margin-bottom: 1.25rem;
  }

  ul {
    margin: 1rem 0 1.5rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.6rem;
    }
  }

  a {
    color: ${brandTheme.colors.primary[600]};
    font-weight: 500;
  }
`;

const CTA = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 2rem;
  text-align: center;
  color: white;
  margin: 3rem 0;

  h3 {
    color: white;
    margin-bottom: 1rem;
    font-size: 1.35rem;
  }

  p {
    margin-bottom: 1.25rem;
    opacity: 0.95;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.75rem;
      border-radius: ${brandTheme.borderRadius.lg};
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;

      &.primary {
        background: white;
        color: ${brandTheme.colors.primary[700]};

        &:hover {
          background: ${brandTheme.colors.primary[50]};
        }
      }

      &.secondary {
        background: rgba(255, 255, 255, 0.15);
        color: white;
        border: 2px solid rgba(255, 255, 255, 0.35);
      }
    }
  }
`;

const FaqBlock = styled.section`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${brandTheme.colors.neutral.gray[200]};

  h2 {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
    color: ${brandTheme.colors.secondary[900]};
  }
`;

const FaqItem = styled.div`
  margin-bottom: 1.5rem;

  strong {
    display: block;
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    color: ${brandTheme.colors.secondary[600]};
    font-size: 1rem;
  }
`;

const TableOfContents = styled.nav`
  background: ${brandTheme.colors.neutral.gray[50]};
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;

  .toc-heading {
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.75rem 0;
    color: ${brandTheme.colors.secondary[900]};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  ol {
    margin: 0;
    padding-left: 1.25rem;
    color: ${brandTheme.colors.secondary[700]};
    font-size: 0.95rem;
    line-height: 1.7;
  }

  a {
    color: ${brandTheme.colors.primary[700]};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RelatedGuides = styled.section`
  margin: 2.5rem 0 0;
  padding: 1.75rem;
  background: ${brandTheme.colors.primary[50]};
  border-radius: ${brandTheme.borderRadius.xl};
  border: 1px solid ${brandTheme.colors.primary[200]};

  h3 {
    font-size: 1.2rem;
    margin: 0 0 1rem 0;
    color: ${brandTheme.colors.secondary[900]};
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
    line-height: 1.8;
    color: ${brandTheme.colors.secondary[700]};
  }
`;

const BoroughLondonGuidePage: React.FC = () => {
  const { routeSlug = '' } = useParams<{ routeSlug: string }>();
  const ctx = useMemo(() => getLondonGuideContext(routeSlug), [routeSlug]);

  const heroImage = useMemo(() => {
    const list = getAllLondonGuideArticleSummaries();
    const found = list.find((a) => a.id === `london/${routeSlug}`);
    return found?.image ?? '/images/resin-surfaces-bound-work1.jpeg';
  }, [routeSlug]);

  useEffect(() => {
    if (ctx) {
      trackBlogView(`London guide: ${ctx.displayName}`);
    }
  }, [ctx]);

  const relatedGuides = useMemo(() => {
    if (!isValidServiceAreaRouteSlug(routeSlug)) return [];
    return getRelatedLondonGuides(routeSlug, 4);
  }, [routeSlug]);

  const jsonLdGraph = useMemo(() => {
    if (!ctx) return null;
    const metaInner = buildGuideMetaDescription(ctx);
    const keywordsInner = buildGuideKeywords(ctx);
    const { displayName: dn, canonicalUrl: cUrl } = ctx;
    const titleInner = `Resin Bound Driveways in ${dn}, London | Local Guide | ZAN RESIN SURFACES LTD`;
    const ogImg = `https://www.resinsurfaceslondon.co.uk${heroImage}`;
    const siteOrigin = 'https://www.resinsurfaceslondon.co.uk';
    const orgId = `${siteOrigin}/#organization`;
    const siteId = `${siteOrigin}/#website`;
    const placeId = `${cUrl}#place`;
    const serviceId = `${cUrl}#service`;
    const breadcrumbId = `${cUrl}#breadcrumb`;
    const faqPageId = `${cUrl}#faqpage`;
    const articleId = `${cUrl}#article`;
    const logoUrl = `${siteOrigin}/images/resin-surfaces-bound-work1.jpeg`;
    const isoPublished = '2025-03-26T09:00:00+00:00';

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': orgId,
          name: 'ZAN RESIN SURFACES LTD',
          url: siteOrigin,
          logo: { '@type': 'ImageObject', url: logoUrl },
          telephone: '+447535935779',
          email: 'info@resinsurfaceslondon.co.uk',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'London',
            addressCountry: 'GB',
          },
        },
        {
          '@type': 'WebSite',
          '@id': siteId,
          name: 'Resin Surfaces London',
          url: siteOrigin,
          inLanguage: 'en-GB',
          publisher: { '@id': orgId },
        },
        {
          '@type': ['Place', 'AdministrativeArea'],
          '@id': placeId,
          name: `${dn}, Greater London`,
          containedInPlace: {
            '@type': 'AdministrativeArea',
            name: 'Greater London',
            addressCountry: 'GB',
          },
        },
        {
          '@type': 'Service',
          '@id': serviceId,
          name: `Resin bound driveway & pathway installation — ${dn}`,
          serviceType: 'Resin bound surfacing',
          description: `Professional permeable resin bound driveways, pathways and commercial surfacing in ${dn} and Greater London.`,
          provider: { '@id': orgId },
          areaServed: { '@id': placeId },
          url: cUrl,
        },
        {
          '@type': 'WebPage',
          '@id': cUrl,
          url: cUrl,
          name: titleInner,
          description: metaInner,
          inLanguage: 'en-GB',
          isPartOf: { '@id': siteId },
          about: { '@id': placeId },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: ogImg,
            width: 1200,
            height: 630,
          },
          breadcrumb: { '@id': breadcrumbId },
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['#guide-article-title', '.guide-speakable-lede'],
          },
        },
        {
          '@type': 'BlogPosting',
          '@id': articleId,
          headline: `Resin Bound Driveways in ${dn}, London: Local Guide`,
          description: metaInner,
          url: cUrl,
          datePublished: isoPublished,
          dateModified: isoPublished,
          inLanguage: 'en-GB',
          wordCount: 2800,
          author: { '@id': orgId },
          publisher: { '@id': orgId },
          image: {
            '@type': 'ImageObject',
            url: ogImg,
            width: 1200,
            height: 630,
          },
          mainEntityOfPage: { '@id': cUrl },
          isPartOf: {
            '@type': 'Blog',
            '@id': `${siteOrigin}/blog/`,
            name: 'Resin Surfaces London Blog',
            url: `${siteOrigin}/blog/`,
          },
          articleSection: 'London',
          keywords: keywordsInner,
          about: { '@id': placeId },
          mentions: { '@id': serviceId },
        },
        {
          '@type': 'FAQPage',
          '@id': faqPageId,
          mainEntity: buildGuideFaqs(ctx).map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: f.answer,
            },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          '@id': breadcrumbId,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${siteOrigin}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${siteOrigin}/blog/`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: `${dn} guide`,
              item: cUrl,
            },
          ],
        },
      ],
    };
  }, [ctx, heroImage]);

  if (!ctx) {
    return <Navigate to="/blog/" replace />;
  }

  const metaDesc = buildGuideMetaDescription(ctx);
  const keywords = buildGuideKeywords(ctx);
  const lead = getLocalLeadParagraph(ctx);
  const faqs = buildGuideFaqs(ctx);
  const { displayName, borough, canonicalUrl, servicePagePath } = ctx;
  const keyAreasList = borough.keyAreas.join(', ');
  const postcodesShort = borough.postcodes.slice(0, 12).join(', ');
  const title = `Resin Bound Driveways in ${displayName}, London | Local Guide | ZAN RESIN SURFACES LTD`;
  const ogImageAbsolute = `https://www.resinsurfaceslondon.co.uk${heroImage}`;
  const ogImageAlt = `Resin bound driveway and surfacing guide for ${displayName}, London`;
  const isoPublished = '2025-03-26T09:00:00+00:00';
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content="ZAN RESIN SURFACES LTD" />
        <meta name="publisher" content="ZAN RESIN SURFACES LTD" />
        <meta name="language" content="en-GB" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={`Resin Bound Driveways in ${displayName}, London: Local Guide`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageAbsolute} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={ogImageAlt} />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="og:locale" content="en_GB" />
        <meta property="article:published_time" content={isoPublished} />
        <meta property="article:modified_time" content={isoPublished} />
        <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:section" content="London" />
        <meta property="article:tag" content={`resin driveways ${displayName}`} />
        <meta property="article:tag" content="resin bound surfacing London" />
        <meta property="article:tag" content="permeable SUDS driveway" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Resin Bound Driveways in ${displayName}, London`} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImageAbsolute} />
        <meta name="twitter:image:alt" content={ogImageAlt} />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />

        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content={`${displayName}, Greater London`} />

        <link rel="alternate" hrefLang="en-GB" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

        {jsonLdGraph ? (
          <script type="application/ld+json">{JSON.stringify(jsonLdGraph)}</script>
        ) : null}
      </Helmet>

      <HeroSectionComponent
        title={`Resin Bound Driveways in ${displayName}`}
        subtitle={`Local planning context, costs, and design ideas for ${displayName} — written for homeowners and commercial clients across London.`}
        badge="London • Permeable surfacing • Expert installation"
        backgroundImage={heroImage}
        primaryButtonText="Get a free quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call us"
        secondaryButtonLink="tel:+447535935779"
      />

      <ArticleContainer>
        <BackLink to="/blog/">
          <ArrowLeft size={18} />
          Back to Blog
        </BackLink>

        <article>
          <ArticleHeader>
            <span className="category">London borough guide</span>
            <h1 id="guide-article-title">Resin Bound Driveways in {displayName}: What Local Property Owners Should Know</h1>
            <p className="excerpt">{metaDesc}</p>
            <div className="meta">
              <span>
                <Calendar size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                <time dateTime="2025-03-26">26 March 2025</time>
              </span>
              <span>
                <Clock size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                14 min read
              </span>
              <span>
                <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {displayName}, Greater London
              </span>
            </div>
          </ArticleHeader>

          <TableOfContents aria-label="On this page">
            <p className="toc-heading">On this page</p>
            <ol>
              <li>
                <a href="#why-resin-bound">Why resin bound suits {displayName} properties</a>
              </li>
              <li>
                <a href="#suds-planning">SUDS, planning &amp; conservation</a>
              </li>
              <li>
                <a href="#costs">Costs in {displayName}</a>
              </li>
              <li>
                <a href="#design">Design &amp; aggregates</a>
              </li>
              <li>
                <a href="#installation">Professional installation</a>
              </li>
              <li>
                <a href="#maintenance">Maintenance</a>
              </li>
              <li>
                <a href="#further-reading">Further reading</a>
              </li>
              <li>
                <a href="#related-guides">More London guides</a>
              </li>
              <li>
                <a href="#local-faq">FAQ</a>
              </li>
            </ol>
          </TableOfContents>

          <ArticleContent>
            <p className="guide-speakable-lede">{lead}</p>

          <h2 id="why-resin-bound">Why resin bound surfacing suits {displayName} properties</h2>
          <p>
            Resin bound paving combines natural stone aggregate with a clear UV-stable resin binder. The mix is
            trowelled to form a smooth, textured surface that stays locked together — unlike loose gravel — while
            remaining water-permeable when installed on an open-graded base. That matters in London, where paved front
            gardens and parking areas must often be designed to reduce surface water run-off.
          </p>
          <p>
            In {displayName}, driveways and forecourts range from compact terraced plots to larger suburban drives. The
            finish is popular where kerb appeal matters: you can choose aggregate colours that complement brick, stone,
            and render. For areas around {keyAreasList}, we regularly see demand for pathways, parking bays, and
            shared courtyards as well as single-car drives.
          </p>

          <h2 id="suds-planning">SUDS, planning, and conservation considerations</h2>
          <p>
            Permeable resin bound systems can support Sustainable Urban Drainage (SUDS) principles by allowing rainwater
            to infiltrate through the surface and into the ground below, provided the underlying layers are designed
            correctly. If you are replacing a traditional front garden with hardstanding, local guidance may apply
            depending on your council and street type.
          </p>
          <p>
            Some streets in London sit within conservation areas or are subject to estate covenants. Where colour,
            texture, or boundary details are sensitive, we can advise on subtle aggregate blends and edging details
            that stay practical for daily use. Always check with your local planning authority if you are unsure —
            especially for listed buildings or substantial changes to visibility splays.
          </p>

          <h2 id="costs">Costs: what moves the price in {displayName}?</h2>
          <p>
            Most quoted prices are given per square metre — but the total project depends on the condition of what is
            already there. If the existing base is sound, free-draining, and level, you may be looking at a
            resurfacing-style scope. If the site needs full excavation, geotextile separation, new sub-base, or complex
            drainage, the groundwork will represent a larger share of the budget.
          </p>
          <p>
            Access and waste removal matter in London: narrow side gates, basement deliveries, and parking suspensions
            can all affect programme and cost. We survey sites across postcodes including {postcodesShort}
            {borough.postcodes.length > 12 ? ' and more' : ''} and give clear written quotes before work begins.
          </p>

          <h2 id="design">Design: aggregates, edgings, and detail</h2>
          <p>
            Aggregate choice affects both appearance and slip resistance. Lighter blends can lift darker brickwork;
            darker blends can look more dramatic in high-contrast schemes. Granite and angular blends are widely used
            for durability. Edging — whether granite, aluminium, or block — keeps the system tidy and protects the
            perimeter of the resin layer.
          </p>
          <p>
            For pathways and shared spaces, consider how the surface interacts with steps, thresholds, and adjacent
            materials. We aim for consistent falls, discreet drainage, and neat lines that still look good after years of
            weather and sweeping.
          </p>

          <h2 id="installation">Installation: what a professional process looks like</h2>
          <p>
            Resin bound is not a generic “pour and spread” product like some DIY kits suggest. Batches must be mixed
            accurately, laid within the correct time window, and finished to the right compaction. Temperature and
            humidity on the day affect cure times — particularly important in spring and autumn when London weather
            can swing between warm afternoons and cold nights.
          </p>
          <p>
            A typical domestic driveway might take a few days to complete once preparation is finished, but
            programmes are always site-specific. We keep clients informed on access, drying times, and when the surface
            can take foot or vehicle traffic.
          </p>

          <h2 id="maintenance">Maintenance through the seasons</h2>
          <p>
            Routine maintenance is straightforward: regular sweeping, occasional washing, and prompt removal of oil or
            tyre marks where possible. Avoid harsh solvents not recommended for resin systems. In autumn, leaf litter
            and organic debris can build up; keeping the surface clear helps drainage and reduces slip risk in winter.
          </p>

          <h2 id="further-reading">Further reading</h2>
          <p>
            For deeper technical background, read our{' '}
            <Link to="/blog/ultimate-guide-resin-bound-surfacing/">ultimate guide to resin bound surfacing</Link>,{' '}
            <Link to="/blog/resin-driveway-cost-guide/">driveway cost guide</Link>, and{' '}
            <Link to="/blog/resin-surfacing-maintenance-tips/">maintenance tips</Link>. Each article complements this
            local overview for {displayName}.
          </p>

          <CTA>
            <h3>Ready for a survey in {displayName}?</h3>
            <p>
              We install resin bound driveways, pathways, and commercial surfacing across Greater London. Ask for a
              no-obligation site visit and quote.
            </p>
            <div className="cta-buttons">
              <Link to="/contact/" className="primary">
                Request a quote
              </Link>
              <a href="tel:+447535935779" className="secondary">
                <Phone size={18} />
                07535 935 779
              </a>
              <Link to={servicePagePath} className="secondary">
                {displayName} service area page
              </Link>
            </div>
          </CTA>

          <RelatedGuides id="related-guides" aria-labelledby="related-guides-title">
            <h3 id="related-guides-title">More London borough guides</h3>
            <p style={{ marginBottom: '0.75rem', color: brandTheme.colors.secondary[600], fontSize: '1rem' }}>
              Explore neighbouring areas — each guide links to our local service page and uses the same technical standards.
            </p>
            <ul>
              {relatedGuides.map((g) => (
                <li key={g.slug}>
                  <Link to={g.blogPath}>
                    Resin bound driveways in {g.displayName}
                  </Link>
                </li>
              ))}
            </ul>
          </RelatedGuides>
        </ArticleContent>

        <FaqBlock id="local-faq">
          <h2>Frequently asked questions — {displayName}</h2>
          {faqs.map((f) => (
            <FaqItem key={f.question}>
              <strong>{f.question}</strong>
              <p>{f.answer}</p>
            </FaqItem>
          ))}
        </FaqBlock>
        </article>
      </ArticleContainer>
    </>
  );
};

export default BoroughLondonGuidePage;
